import React, { useCallback, useState } from 'react';
import styled, { css } from 'styled-components';
import { ReactComponent as PlayIcon } from '@ubeya/shared-web/assets/play-icon.svg';

const ThumbWrapper = styled.div`
  position: relative;
  overflow: hidden;
  cursor: ${({ $clickable }) => ($clickable ? 'pointer' : 'default')};

  ${({ $fill, $isWide, theme }) =>
    $fill
      ? css`
          width: 100%;
          aspect-ratio: ${$isWide ? '2 / 1' : 1};
          grid-column: ${$isWide ? '1 / -1' : 'auto'};
          border-radius: 6px;
        `
      : css`
          width: 60px;
          height: 60px;
          border-radius: 8px;
          border: 1px solid ${theme.colors.grey2};
        `}
`;

const Thumb = styled.video`
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
`;

const DurationOverlay = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  display: flex;
  justify-content: flex-end;
  padding: 4px 6px;
  background: linear-gradient(to top, rgba(0, 0, 0, 0.6), rgba(0, 0, 0, 0));
  pointer-events: none;
`;

const DurationLabel = styled.span`
  color: ${({ theme }) => theme.colors.white};
  font-size: 11px;
  font-weight: 500;
  line-height: 14px;
`;

const PlayButton = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;
`;

const PlayIconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-left: 6px;

  svg {
    width: 25px;
    height: auto;
  }
`;

const formatVideoDuration = (seconds) => {
  const totalSeconds = Math.round(seconds);
  const minutes = Math.floor(totalSeconds / 60);
  const remainingSeconds = totalSeconds % 60;
  return `${minutes}:${String(remainingSeconds).padStart(2, '0')}`;
};

const VideoPreviewThumb = ({ src, alt, onClick, fill = false, isWide = false, withPlayButton = false }) => {
  const [duration, setDuration] = useState(null);

  const handleLoadedMetadata = useCallback((event) => {
    const seconds = event.target.duration;
    if (Number.isFinite(seconds)) setDuration(seconds);
  }, []);

  return (
    <ThumbWrapper $fill={fill} $isWide={isWide} $clickable={!!onClick} onClick={onClick}>
      <Thumb src={src} muted playsInline preload="metadata" aria-label={alt} onLoadedMetadata={handleLoadedMetadata} />
      {withPlayButton && (
        <PlayButton>
          <PlayIconWrapper>
            <PlayIcon />
          </PlayIconWrapper>
        </PlayButton>
      )}
      {duration !== null && (
        <DurationOverlay>
          <DurationLabel>{formatVideoDuration(duration)}</DurationLabel>
        </DurationOverlay>
      )}
    </ThumbWrapper>
  );
};

export default VideoPreviewThumb;
